import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Badge,
    Box,
    Button,
    Column,
    Columns,
    Dropdown,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { CustomerResponse } from "../../services/customerService";

interface CustomerListTableProps {
    customerList?: Array<CustomerResponse> | null;
    onCreateCustomer?: () => void;
    onEditCustomer?: (customer: CustomerResponse) => void;
}

const CustomerListTable: React.FC<CustomerListTableProps> = ({
    customerList,
    onCreateCustomer,
    onEditCustomer,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState<string>("all");

    const filteredCustomers = useMemo(() => {
        if (!customerList) {
            return customerList;
        }

        return customerList.filter((customer) => {
            const normalizedSearch = searchTerm.trim().toLowerCase();
            const normalizedStatus = Boolean(customer.cus_active) ? "active" : "inactive";

            const matchesSearch =
                !normalizedSearch ||
                customer.cus_code?.toLowerCase().includes(normalizedSearch) ||
                customer.cus_name?.toLowerCase().includes(normalizedSearch) ||
                customer.cus_title?.toLowerCase().includes(normalizedSearch) ||
                customer.cus_email?.toLowerCase().includes(normalizedSearch) ||
                customer.cus_tep1?.toLowerCase().includes(normalizedSearch);

            const matchesStatus = selectedStatus === "all" || normalizedStatus === selectedStatus;

            return matchesSearch && matchesStatus;
        });
    }, [customerList, searchTerm, selectedStatus]);

    const showActions = typeof onEditCustomer === "function";

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        {onCreateCustomer ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateCustomer}
                                >
                                    {t("page.customer_management.list.button.create_new", {
                                        defaultValue: "Create New Customer",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center" gap="medium">
                        <TextField
                            placeholder={t("page.customer_management.list.search.placeholder", {
                                defaultValue: "Search Customers...",
                            })}
                            aria-label={t("page.customer_management.list.search.aria", {
                                defaultValue: "Search Customers",
                            })}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            value={searchTerm}
                        />
                        <Dropdown
                            id="customer-status-filter"
                            aria-label={t("page.customer_management.list.status_filter.aria", {
                                defaultValue: "Status Filter",
                            })}
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.currentTarget.value)}
                        >
                            <option value="all">
                                {t("page.customer_management.list.status_filter.all", {
                                    defaultValue: "All Status",
                                })}
                            </option>
                            <option value="active">
                                {t("page.customer_management.list.status_filter.active", {
                                    defaultValue: "Active",
                                })}
                            </option>
                            <option value="inactive">
                                {t("page.customer_management.list.status_filter.inactive", {
                                    defaultValue: "Inactive",
                                })}
                            </option>
                        </Dropdown>
                    </Box>
                </Column>
            </Columns>

            <Table
                label={t("page.customer_management.list.table.label", {
                    defaultValue: "Customers List Table",
                })}
            >
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.code", {
                                    defaultValue: "Code",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.accno", {
                                    defaultValue: "Account No",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.name", {
                                    defaultValue: "Customer Name",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.title", {
                                    defaultValue: "Title",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.group", {
                                    defaultValue: "Group",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.email", {
                                    defaultValue: "Email",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>
                                {t("page.customer_management.list.table.headers.phone", {
                                    defaultValue: "Phone",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>
                                {t("page.customer_management.list.table.headers.balance", {
                                    defaultValue: "Balance",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>
                                {t("page.customer_management.list.table.headers.points", {
                                    defaultValue: "Points",
                                })}
                            </Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>
                                {t("page.customer_management.list.table.headers.status", {
                                    defaultValue: "Status",
                                })}
                            </Text>
                        </TableHeaderCell>
                        {showActions ? (
                            <TableHeaderCell align="right">
                                <Text>
                                    {t("page.customer_management.list.table.headers.actions", {
                                        defaultValue: "Actions",
                                    })}
                                </Text>
                            </TableHeaderCell>
                        ) : null}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {filteredCustomers && filteredCustomers.length > 0 ? (
                        filteredCustomers.map((customer) => {
                            const normalizedStatus = Boolean(customer.cus_active) ? "active" : "inactive";
                            const isActive = normalizedStatus === "active";

                            return (
                                <TableRow key={customer.cus_id ?? customer.cus_code ?? "customer-row"}>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_code ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_accno ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_name ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_title ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_groupid ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_email ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {customer.cus_tep1 ||
                                                t("page.customer_management.list.table.na", {
                                                    defaultValue: "N/A",
                                                })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Text>{customer.cus_balance ?? "0.00"}</Text>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Text>{customer.cus_points ?? "0.00"}</Text>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Badge tone={isActive ? "positive" : "caution"}>
                                            {isActive
                                                ? t("page.customer_management.list.badge.active", {
                                                      defaultValue: "Active",
                                                  })
                                                : t("page.customer_management.list.badge.inactive", {
                                                      defaultValue: "Inactive",
                                                  })}
                                        </Badge>
                                    </TableCell>
                                    {showActions ? (
                                        <TableCell align="right">
                                            <Inline space="small">
                                                <TooltipRenderer
                                                    tooltip={
                                                        <Text>
                                                            {t("page.customer_management.list.tooltip.edit", {
                                                                defaultValue: "Edit Customer",
                                                            })}
                                                        </Text>
                                                    }
                                                >
                                                    {({ triggerProps }) => (
                                                        <Box
                                                            onClick={() => {
                                                                if (onEditCustomer) {
                                                                    onEditCustomer(customer);
                                                                }
                                                            }}
                                                            cursor="pointer"
                                                            {...triggerProps}
                                                        >
                                                            <IconEdit tone="info" />
                                                        </Box>
                                                    )}
                                                </TooltipRenderer>
                                            </Inline>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colspan={showActions ? 11 : 10} align="center">
                                <Text>
                                    {t("page.customer_management.list.empty", {
                                        defaultValue: "No customers found.",
                                    })}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default CustomerListTable;
